// User-detail screen: for one user (via the route param) within the active org,
// view their assigned roles + the roles available to add, and assign/remove them.
// This is the page-based successor to the old UserRolesPage dropdown flow — the
// assign/remove logic is carried over verbatim; only the user is now chosen by
// navigating from the Users list instead of a <select>.
//
// Roles UI: what the user HAS is a row of removable chips (the role and the
// control that detaches it are one object, with a count so "how many" is
// readable at a glance). What they COULD have is a searchable picker of the
// unassigned roles — one click on a row assigns it, with a toast confirming.
// The previous version listed every available role as a chip + "Attribuer"
// button pair, which does not survive an org with a large role catalogue.

import { useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { adminApi } from "../api/endpoints.js"
import type { PermissionDto, RoleDto, UserDto, UserTeamSummaryDto } from "../api/types.js"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { ShopAccessCard } from "../components/ShopAccessCard.js"
import { fr } from "../i18n/fr.js"
import { messageForError } from "../lib/errors.js"
import { domainLabel, groupByDomain } from "../lib/grouping.js"
import { useAsync } from "../lib/useAsync.js"
import { userFullName } from "../lib/users.js"
import { useOrg } from "../state/org.js"
import { PlusIcon } from "../ui/icons.js"
import { Alert, Button, Card, Chip, RemovableChip, Spinner } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"

/** How a role is named in the UI: its display name, falling back to the key. */
function roleLabel(role: RoleDto): string {
  return role.displayName || role.name
}

export function UserDetailPage() {
  const { userId = "" } = useParams()
  const navigate = useNavigate()
  const notify = useNotify()
  const writesEnabled = useWritesEnabled()
  const { activeOrg, orgs } = useOrg()

  const users = useAsync<UserDto[]>(
    () => (activeOrg ? adminApi.listUsers(activeOrg) : Promise.resolve([])),
    [activeOrg]
  )
  const user = useMemo(
    () => (users.data ?? []).find((u) => u.id === userId) ?? null,
    [users.data, userId]
  )

  const allRoles = useAsync<RoleDto[]>(
    () => (activeOrg ? adminApi.listRoles(activeOrg) : Promise.resolve([])),
    [activeOrg]
  )
  const assigned = useAsync<RoleDto[]>(
    () => (userId && activeOrg ? adminApi.listUserRoles(userId, activeOrg) : Promise.resolve([])),
    [userId, activeOrg]
  )
  // Read-only: the teams (within the active org) this user belongs to — the
  // user-side mirror of a team's member list. Membership is managed from the
  // Teams screen; here it is shown as chips only.
  const teams = useAsync<UserTeamSummaryDto[]>(
    () => (userId && activeOrg ? adminApi.listUserTeams(userId, activeOrg) : Promise.resolve([])),
    [userId, activeOrg]
  )

  const [busy, setBusy] = useState(false)
  // Client-side filter over the unassigned roles. The whole role catalogue for
  // the org is already in memory (allRoles), so this needs no extra request and
  // no debounce — unlike the users list, which pages server-side.
  const [roleQuery, setRoleQuery] = useState("")

  const assignedIds = useMemo(
    () => new Set((assigned.data ?? []).map((r) => r.id)),
    [assigned.data]
  )
  const available = useMemo(
    () => (allRoles.data ?? []).filter((r) => !assignedIds.has(r.id)),
    [allRoles.data, assignedIds]
  )

  // Match on both the display name and the technical key: admins search for
  // "shop_supervisor" as readily as for "Superviseur".
  const matches = useMemo(() => {
    const q = roleQuery.trim().toLowerCase()
    if (!q) return available
    return available.filter(
      (r) => (r.displayName ?? "").toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
    )
  }, [available, roleQuery])

  const orgName = useMemo(() => orgs.find((o) => o.id === activeOrg)?.name ?? "", [orgs, activeOrg])

  // Effective permissions = the union of the permissions granted by every assigned
  // role. RoleDto only carries a permission count, so we resolve each role's detail
  // (bounded by the user's assigned-role count) and dedupe by permission id.
  const assignedKey = (assigned.data ?? []).map((r) => r.id).join(",")
  const permissions = useAsync<PermissionDto[]>(async () => {
    const roles = assigned.data ?? []
    if (roles.length === 0) return []
    const details = await Promise.all(roles.map((r) => adminApi.getRole(r.id)))
    const byId = new Map<string, PermissionDto>()
    for (const detail of details) {
      for (const perm of detail.permissions) byId.set(perm.id, perm)
    }
    return [...byId.values()]
  }, [assignedKey])

  // `successMsg` is the only addition to the write path: assigning/removing used
  // to succeed silently, so the only signal was the chip list re-rendering. The
  // writesEnabled guard and the org scoping are unchanged.
  async function mutate(action: () => Promise<unknown>, failMsg: string, successMsg: string) {
    if (!writesEnabled) return
    setBusy(true)
    try {
      await action()
      notify.success(successMsg)
      assigned.reload()
    } catch (err) {
      notify.error(messageForError(err, failMsg))
    } finally {
      setBusy(false)
    }
  }

  function assign(role: RoleDto) {
    if (!activeOrg) return
    void mutate(
      () => adminApi.assignUserRole({ userId, roleId: role.id, organizationId: activeOrg }),
      fr.userRoles.assignError,
      fr.userRoles.assignSuccess(roleLabel(role))
    )
  }

  function remove(role: RoleDto) {
    if (!activeOrg) return
    void mutate(
      () => adminApi.removeUserRole({ userId, roleId: role.id, organizationId: activeOrg }),
      fr.userRoles.removeError,
      fr.userRoles.removeSuccess(roleLabel(role))
    )
  }

  const permissionGroups = useMemo(() => groupByDomain(permissions.data ?? []), [permissions.data])

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/users" className="btn btn--ghost btn--sm">
          {fr.users.detail.back}
        </Link>
      </div>

      {users.loading ? <Spinner /> : null}
      {users.error ? <Alert variant="error">{fr.users.error}</Alert> : null}

      {users.data && !users.loading && !user ? (
        <Card>
          <strong>{fr.users.detail.notFoundTitle}</strong>
          <p className="muted" style={{ marginTop: 8 }}>
            {fr.users.detail.notFound}
          </p>
          <Button variant="primary" onClick={() => navigate("/users")}>
            {fr.users.detail.back}
          </Button>
        </Card>
      ) : null}

      {user ? (
        <>
          <div className="page-header">
            <div>
              <h1>{userFullName(user)}</h1>
              <p>
                <span className="mono">{user.email}</span>
                {orgName ? ` · ${fr.users.detail.orgContext(orgName)}` : null}
              </p>
            </div>
          </div>

          <div className="stack">
            {/* What the user HAS: chips that carry their own remove control. */}
            <Card>
              <div className="card__header">
                <strong>{fr.userRoles.assignedTitle}</strong>
                {assigned.data && !assigned.loading ? (
                  <span className="muted" style={{ fontSize: 13 }}>
                    {fr.userRoles.assignedCount(assigned.data.length)}
                  </span>
                ) : null}
              </div>
              {assigned.loading ? <Spinner /> : null}
              {assigned.error ? <Alert variant="error">{fr.userRoles.error}</Alert> : null}
              {assigned.data && assigned.data.length === 0 && !assigned.loading ? (
                <p className="muted" style={{ margin: 0 }}>
                  {fr.userRoles.empty}
                </p>
              ) : null}
              <div className="row" style={{ gap: 6 }}>
                {(assigned.data ?? []).map((role) => (
                  <RemovableChip
                    key={role.id}
                    system={role.isSystem}
                    title={role.name}
                    removeLabel={fr.userRoles.removeLabel(roleLabel(role))}
                    disabled={!writesEnabled || busy}
                    onRemove={() => remove(role)}
                  >
                    {roleLabel(role)}
                  </RemovableChip>
                ))}
              </div>
            </Card>

            {/* Shops assigned directly to this user (below the roles card). */}
            {activeOrg ? (
              <ShopAccessCard
                org={activeOrg}
                roleIds={(assigned.data ?? []).map((r) => r.id)}
                hint={fr.shops.userHint}
                reloadKey={`${userId}:${activeOrg}`}
                loadShops={() => adminApi.listUserShops(userId, activeOrg)}
                saveShops={(shopIds) =>
                  adminApi.setUserShops({ userId, organizationId: activeOrg, shopIds })
                }
              />
            ) : null}

            {/* The teams this user belongs to (read-only mirror of the members list). */}
            <Card>
              <div className="card__header">
                <strong>{fr.users.detail.teamsTitle}</strong>
                {teams.data && !teams.loading ? (
                  <span className="muted" style={{ fontSize: 13 }}>
                    {teams.data.length}
                  </span>
                ) : null}
              </div>
              <p className="muted" style={{ margin: "4px 0 0" }}>
                {fr.users.detail.teamsHint}
              </p>
              {teams.loading ? <Spinner /> : null}
              {teams.error ? <Alert variant="error">{fr.users.detail.teamsError}</Alert> : null}
              {teams.data && teams.data.length === 0 && !teams.loading ? (
                <p className="muted" style={{ margin: "8px 0 0" }}>
                  {fr.users.detail.teamsEmpty}
                </p>
              ) : null}
              {teams.data && teams.data.length > 0 ? (
                <div className="row" style={{ gap: 6, marginTop: 12 }}>
                  {teams.data.map((team) => (
                    <Chip key={team.id}>{team.name}</Chip>
                  ))}
                </div>
              ) : null}
            </Card>

            {/* What the user COULD have: a searchable picker, one click to assign. */}
            <Card>
              <div className="card__header">
                <strong>{fr.userRoles.availableTitle}</strong>
                <span className="muted" style={{ fontSize: 13 }}>
                  {fr.userRoles.assignHint}
                </span>
              </div>
              {available.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  {fr.userRoles.allAssigned}
                </p>
              ) : (
                <div className="role-picker">
                  <input
                    type="search"
                    className="field__input"
                    aria-label={fr.userRoles.assignSearchLabel}
                    placeholder={fr.userRoles.assignSearchPlaceholder}
                    value={roleQuery}
                    onChange={(e) => setRoleQuery(e.target.value)}
                  />
                  {matches.length === 0 ? (
                    <p className="muted" style={{ margin: 0 }}>
                      {fr.userRoles.noMatch}
                    </p>
                  ) : (
                    <div className="role-picker__list">
                      {matches.map((role) => (
                        <button
                          key={role.id}
                          type="button"
                          className="role-option"
                          // Names the role, not just the action: every row would
                          // otherwise be an identical "Attribuer" to a screen reader.
                          aria-label={fr.userRoles.assignLabel(roleLabel(role))}
                          disabled={!writesEnabled || busy}
                          onClick={() => assign(role)}
                        >
                          <span className="role-option__main">
                            <span className="role-option__name">{roleLabel(role)}</span>
                            <span className="mono subtitle">{role.name}</span>
                          </span>
                          {role.isSystem ? <Chip system>{fr.roles.systemBadge}</Chip> : null}
                          <span className="role-option__add">
                            <PlusIcon />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            <Card>
              <strong>{fr.users.detail.permissionsTitle}</strong>
              <p className="muted" style={{ margin: "4px 0 0" }}>
                {fr.users.detail.permissionsHint}
              </p>
              <div style={{ marginTop: 12 }}>
                {permissions.loading ? <Spinner /> : null}
                {!permissions.loading && permissions.data && permissionGroups.length === 0 ? (
                  <p className="muted" style={{ margin: 0 }}>
                    {fr.users.detail.permissionsEmpty}
                  </p>
                ) : null}
                <div className="stack">
                  {permissionGroups.map((group) => (
                    <div key={group.domain}>
                      <div className="field__label" style={{ marginBottom: 8 }}>
                        {domainLabel(group.domain, fr.permissions.domain)}
                      </div>
                      <div className="row">
                        {group.permissions.map((perm) => (
                          <Chip key={perm.id} mono title={perm.description ?? undefined}>
                            {perm.key}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  )
}
